import React from 'react';
import { apiRequest } from '../actions/apiActions';
import { showLoading, hideLoading, addUiMsg } from '../actions/uiActions';
import { apiUrls, apiVersion } from '../../apiUrls';
import {
  EDIT_DEVICE_ASSOCIATION,
  PROCESS_DEVICE_ASSOCIATION,
  PROCESS_DELETE_DEVICE_ASSOCIATION,
  CREATE_DEVICE_ASSOCIATION,
  DELETE_DEVICE_ASSOCIATION,
  DEVICE_ERROR,
  createDeviceAssociation,
  LOAD_DEVICE_ASSOCIATIONS,
  PROCESS_DEVICE_ASSOCIATIONS,
  insertDeviceAssociations,
  deleteDeviceAssociation,
  removeDeviceAssociation,
} from '../actions/deviceActions';
import { messageTypes } from '../../components/ui/AlertMessage';
import {
  unitNameText,
  wasUpdatedText,
  userText,
} from '../../components/shared/SharedInternationalizations';
import { loadAlertDeviceDetails } from '../actions/alertDeviceActions';
import { loadAlarmDeviceDetails } from '../actions/alarmDeviceActions';
import { getUser } from '../actions/userActions';
import { loadUnit } from '../actions/unitActions';
import { redirect } from '../../utilities/general';

/**
 * This middleware is used to process the device associations that have been
 * created/deleted in the alert and alarm device association forms
 */
export const editDeviceAssociation = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_DEVICE_ASSOCIATION) {
    const payload = action.payload;
    const navigate = action.navigate;
    const previous = action.previous;
    const formSteps = action.formSteps;

    const devices = payload.values.devices;
    const initialDevices = payload.values.initialDevices;

    devices.map((d) => {// eslint-disable-line
      if (!initialDevices.includes(d)) {
        const data = {};
        if (payload.type === 'user') {
          data.assigned_user = payload.values.associateId;
        } else if (payload.type === 'unit') {
          data.assigned_unit = payload.values.associateId;
        }
        if (payload.values.type === 'alert') {
          data.alert_device = d;
        } else if (payload.values.type === 'alarm') {
          data.alarm_device = d;
        }

        store.dispatch(
          createDeviceAssociation(data, navigate, previous, formSteps)
        );
      }
    });

    let associationType = 'assigned_user';
    if (payload.type === 'unit') {
      associationType = 'assigned_unit';
    }
    let payloadType = 'alert_device';
    if (payload.values.type === 'alarm') {
      payloadType = 'alarm_device';
    }
    const associations = store.getState().devices.deviceAssociations;
    const associationsArr = Object.values(associations);
    const filteredAssociations = associationsArr.filter(
      (a) => a[associationType] === payload.values.associateId
    );
    initialDevices.map((d) => {// eslint-disable-line
      if (!devices.includes(d)) {
        const deviceAssociation = filteredAssociations.filter((association) => {
          if (association[payloadType] === d) {
            return true;
          } else {
            return false;
          }
        });

        store.dispatch(
          deleteDeviceAssociation(
            deviceAssociation,
            navigate,
            previous,
            formSteps
          )
        );
      }
    });
  }
};
/**
 * Create a device association
 *
 * Submits the api call to create a device association and triggers the
 * show loading action. This is used for both alert devices and alarm
 * devices as well as for both users and units.
 */
export const createDeviceAssociationMdl = (store) => (next) => (action) => {
  next(action);
  if (action.type === CREATE_DEVICE_ASSOCIATION) {
    const body = action.payload;
    const meta = {
      method: 'POST',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].deviceassociation,
      onSuccess: PROCESS_DEVICE_ASSOCIATION,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      previous: action.previous,
      formSteps: action.formSteps,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Delete device association
 *
 * Sends a DELETE request to the api to delete a device association and triggers
 * the show loading action. This is used for both alert devices and alarm
 * devices as well as for both users and units.
 *
 * In the metadata being passed to the api call there is an item called "data"
 * that contains the body data that is also sent to the api. This object is
 * used in the process middleware to allow correct processing and updating
 * of data.
 */
export const deleteDeviceAssociationMdl = (store) => (next) => (action) => {
  next(action);
  if (action.type === DELETE_DEVICE_ASSOCIATION) {
    const body = action.payload[0];
    const meta = {
      method: 'DELETE',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].deviceassociation +
        body.id +
        '/',
      onSuccess: PROCESS_DELETE_DEVICE_ASSOCIATION,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
      navigate: action.navigate,
      previous: action.previous,
      formSteps: action.formSteps,
      data: body,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Process device association
 *
 * This processes the device association. Because the association api
 * call is just creating an association, it doesn't return any information
 * about the device or user/unit. So once the data is receieved, it is
 * then checked to see if the association is for an alert or alarm device
 * then it triggers an api call to get the details for that specific device.
 *
 * It cancels the loading for the initial call and then checks to see if the
 * association was for a user or a unit and triggers an api call for that
 * specific user/unit. By retrieving the full user/unit object it will then
 * pull all of the associated devices, which will include the newly associated
 * device and the device will display correctly in the interface.
 *
 * After triggering the call for the user/unit data, it then generates the url
 * for the user/unit that the association was for and triggers a redirect to
 * move the interface back to the display page for the user/unit. It also
 * creates an interface confirmation message that the association was successful.
 *
 * The reason the device details are fetched is because in the device
 * details, there is an array of ids for any users or units that the
 * devices is associated with. So to ensure that there is no confusion
 * in the interface regarding if the device is associated to anything,
 * the updated details are fetched which will ensure that the newly
 * removed association is reflected.
 */

export const processDeviceAssociation = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_DEVICE_ASSOCIATION) {
    const data = action.payload;
    if (data.alert_device) {
      store.dispatch(loadAlertDeviceDetails(data.alert_device));
    }
    if (data.alarm_device) {
      store.dispatch(loadAlarmDeviceDetails(data.alarm_device));
    }
    store.dispatch(hideLoading());

    let url = '/';
    if (data.assigned_user) {
      store.dispatch(getUser(data.assigned_user));
      url = '/users/' + data.assigned_user + '/view/';
    } else if (data.assigned_unit) {
      store.dispatch(loadUnit(data.assigned_unit));
      url = '/units/' + data.assigned_unit + '/view/';
    }
    redirect(action, url);

    if (data.assigned_user) {
      const userObj = store.getState().users.users[data.assigned_user];
      const messageText = (
        <React.Fragment>
          {userText}:
          <span className="text-italic margin-left-5 margin-right-5">
            "{userObj.first_name} {userObj.last_name}"
          </span>
          {wasUpdatedText}
        </React.Fragment>
      );
      const message = {
        message: messageText,
        messageType: messageTypes.success,
      };

      store.dispatch(addUiMsg(message));
    } else if (data.assigned_unit) {
      const unitObj = store.getState().units.units[data.assigned_unit];
      const messageText = (
        <React.Fragment>
          {unitNameText}:
          <span className="text-italic margin-left-5 margin-right-5">
            "{unitObj.name}"
          </span>
          {wasUpdatedText}
        </React.Fragment>
      );
      const message = {
        message: messageText,
        messageType: messageTypes.success,
      };

      store.dispatch(addUiMsg(message));
    }
  }
};

/**
 * Process a device association deletion
 *
 * When the association is deleted, no information is returned. So
 * the api middleware returns the metadata sent in the original api
 * call. This includes the device id and the user/unit id. Using this
 * data, the middleware checks to see if it's an alert or alarm device
 * and sends an api call to get the appropriate data.
 *
 * It then cancels the loading bar and checks to see if the association
 * was for a user or a unit. It triggers a call for the user/unit's
 * data and generates a url for that user/unit's display page and
 * triggers a redirect to that page. It then creates a confirmation
 * message that the association was removed.
 *
 * The reason the device details are fetched is because in the device
 * details, there is an array of ids for any users or units that the
 * devices is associated with. So to ensure that there is no confusion
 * in the interface regarding if the device is associated to anything,
 * the updated details are fetched which will ensure that the newly
 * removed association is reflected.
 */

export const processDeviceAssociationDeletion = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_DELETE_DEVICE_ASSOCIATION) {
    const data = action.meta.data;

    store.dispatch(removeDeviceAssociation(data.id));

    if (data.alert_device) {
      store.dispatch(loadAlertDeviceDetails(data.alert_device));
    }
    if (data.alarm_device) {
      store.dispatch(loadAlarmDeviceDetails(data.alarm_device));
    }
    store.dispatch(hideLoading());

    let url = '/';
    if (data.assigned_user) {
      store.dispatch(getUser(data.assigned_user));
      url = '/users/' + data.assigned_user + '/view/';
    } else if (data.assigned_unit) {
      store.dispatch(loadUnit(data.assigned_unit));
      url = '/units/' + data.assigned_unit + '/view/';
    }

    redirect(action, url);

    if (data.assigned_user) {
      const userObj = store.getState().users.users[data.assigned_user];
      const messageText = (
        <React.Fragment>
          {userText}:
          <span className="text-italic margin-left-5 margin-right-5">
            "{userObj.first_name} {userObj.last_name}"
          </span>
          {wasUpdatedText}
        </React.Fragment>
      );
      const message = {
        message: messageText,
        messageType: messageTypes.success,
      };

      store.dispatch(addUiMsg(message));
    } else if (data.assigned_unit) {
      const unitObj = store.getState().units.units[data.assigned_unit];
      const messageText = (
        <React.Fragment>
          {unitNameText}:
          <span className="text-italic margin-left-5 margin-right-5">
            "{unitObj.name}"
          </span>
          {wasUpdatedText}
        </React.Fragment>
      );
      const message = {
        message: messageText,
        messageType: messageTypes.success,
      };

      store.dispatch(addUiMsg(message));
    }
  }
};

/**
 * Load device associations
 *
 * Triggers an api call to retrieve the list of device associations.
 * This is triggered in the device association form.
 */
export const loadDeviceAssociations = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_DEVICE_ASSOCIATIONS) {
    const body = action.payload;
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].deviceassociation,
      onSuccess: PROCESS_DEVICE_ASSOCIATIONS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      previous: action.previous,
      formSteps: action.formSteps,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Process device associations
 *
 * This takes the returned data from the api and processes it. The first check
 * is to see if there are paged results. If so, it triggers an api call to retrieve
 * the next page of results. If no more pages, it triggers to cancel the loading
 * bar.
 *
 * Then it iterates through the data returned and adds each item to an object
 * with the device association as the key and the association object as the value.
 * Then it dispatches the processed object to be inserted into the Redux store.
 */

export const processDeviceAssociations = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_DEVICE_ASSOCIATIONS) {
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_DEVICE_ASSOCIATIONS,
        onError: DEVICE_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      store.dispatch(hideLoading());
    }
    const data = action.payload.results;
    const processedData = {};
    data.map((d) => {// eslint-disable-line
      processedData[d.id] = d;
    });
    store.dispatch(insertDeviceAssociations(processedData));
  }
};

export const devicesMdl = [
  editDeviceAssociation,
  createDeviceAssociationMdl,
  deleteDeviceAssociationMdl,
  processDeviceAssociation,
  processDeviceAssociationDeletion,
  loadDeviceAssociations,
  processDeviceAssociations,
];
